import { useCallback, useEffect, useRef, useState } from 'react';
import { getPortalRequirements, PortalRequirement } from '../api/portalService';

export type UsePortalRequirementsResult = {
  data: PortalRequirement[];
  loading: boolean;
  error: Error | null;
  settled: boolean;
  refetch: () => void;
};

/**
 * Loads the client-visible requirements list when the board is 03A or 03C.
 * Skips the network when `enabled` is false.
 *
 * Same first-load vs refetch split as `usePortalStatus` (OUW-70): a refetch
 * after deliver/upload must not flash a spinner or wipe the last good list.
 */
export function usePortalRequirements(
  quoteId: string,
  options?: { enabled?: boolean },
): UsePortalRequirementsResult {
  const enabled = (options?.enabled ?? true) && Boolean(quoteId.trim());
  const [data, setData] = useState<PortalRequirement[]>([]);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<Error | null>(null);
  const [settled, setSettled] = useState(false);
  const [nonce, setNonce] = useState(0);
  const loadedOnce = useRef(false);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      setSettled(false);
      return undefined;
    }

    let cancelled = false;
    const isRefresh = loadedOnce.current;
    if (isRefresh) {
      setError(null);
    } else {
      setLoading(true);
      setSettled(false);
      setError(null);
    }

    getPortalRequirements(quoteId)
      .then((items) => {
        if (cancelled) return;
        loadedOnce.current = true;
        setData(items);
        setError(null);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (!isRefresh) setData([]);
        setError(err instanceof Error ? err : new Error('Failed to load portal requirements'));
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
        setSettled(true);
      });

    return () => {
      cancelled = true;
    };
  }, [quoteId, enabled, nonce]);

  const refetch = useCallback(() => setNonce((n) => n + 1), []);

  return { data, loading, error, settled, refetch };
}
